import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, ArrowLeft, CheckCircle2, AlertCircle, X } from 'lucide-react';
import BaseUrl from '../Baseurl/baseurl';
import { useCustomTheme } from '../../context/ThemeContext';
import { Card, FormField, Input, Button, Eyebrow, Loader, Container } from '../ui';
import Header from './Header';
import Footer from './Footer';

const UpdateCredentialsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { mode } = useCustomTheme();

  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [formData, setFormData] = useState({
    email: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BaseUrl}/api/member/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();

        if (data.success && data.data) {
          setMember(data.data);
          setFormData(prev => ({
            ...prev,
            email: data.data.email || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching member:', err);
        setSnackbar({
          open: true,
          message: 'Failed to load member data',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [id]);

  const handleClickShowPassword = (field) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = { ...errors };
    let isValid = true;

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    // Old password validation
    if (!formData.oldPassword) {
      newErrors.oldPassword = 'Old password is required';
      isValid = false;
    }

    // New password validation
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
      isValid = false;
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${BaseUrl}/api/member/update/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: formData.email,
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message || 'Credentials updated successfully!',
          severity: 'success'
        });

        // Update local storage if email changed
        if (formData.email !== member.email) {
          const memberData = JSON.parse(localStorage.getItem('memberData'));
          if (memberData) {
            localStorage.setItem('memberData', JSON.stringify({
              ...memberData,
              email: formData.email
            }));
          }
        }
      } else {
        throw new Error(data.message || 'Failed to update credentials');
      }
    } catch (err) {
      console.error('Error updating credentials:', err);
      setSnackbar({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading && !member) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Loader label="Loading…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg pb-16 text-text">
      <Header />
      {/* Dark hero band */}
      <div className="on-dark bg-navy relative px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="absolute left-4 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full text-ink ring-1 ring-inset ring-line/20 transition-colors hover:bg-primary/10"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Eyebrow className="block text-center">Account</Eyebrow>
        <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink text-center">
          {t('Update Login Credentials')}
        </h1>
      </div>

      <Container width="narrow" className="mt-8 max-w-[520px]">
        <Card padding="lg">
          <h2 className="font-display text-xl font-semibold tracking-tight text-ink">
            {t('Update Email')} <em className="kbc-serif">Password</em>
          </h2>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <FormField label="Email Address" htmlFor="email" error={errors.email}>
              <Input
                id="email"
                name="email"
                type="text"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                placeholder="Enter your email address"
              />
            </FormField>

            <FormField label="Old Password" htmlFor="old-password" error={errors.oldPassword}>
              <Input
                id="old-password"
                name="oldPassword"
                type={showPassword.oldPassword ? 'text' : 'password'}
                value={formData.oldPassword}
                onChange={handleChange}
                error={!!errors.oldPassword}
                placeholder="Enter your old password"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => handleClickShowPassword('oldPassword')}
                    className="text-muted transition-colors hover:text-ink"
                    aria-label={showPassword.oldPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword.oldPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                }
              />
            </FormField>

            <FormField label="New Password" htmlFor="new-password" error={errors.newPassword}>
              <Input
                id="new-password"
                name="newPassword"
                type={showPassword.newPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                error={!!errors.newPassword}
                placeholder="Enter your new password"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => handleClickShowPassword('newPassword')}
                    className="text-muted transition-colors hover:text-ink"
                    aria-label={showPassword.newPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword.newPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                }
              />
            </FormField>

            <FormField label="Confirm New Password" htmlFor="confirm-password" error={errors.confirmPassword}>
              <Input
                id="confirm-password"
                name="confirmPassword"
                type={showPassword.confirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                placeholder="Re-enter your new password"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => handleClickShowPassword('confirmPassword')}
                    className="text-muted transition-colors hover:text-ink"
                    aria-label={showPassword.confirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword.confirmPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                }
              />
            </FormField>

            <Button type="submit" fullWidth size="lg" loading={loading} disabled={loading}>
              {loading ? 'Updating…' : 'Update Credentials'}
            </Button>
          </form>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted">
            {/* {t('credentialsNote') || 'Note: Changing your email will update your login username. Changing your password will log you out of all devices.'} */}
          </p>
        </div>
      </Container>

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-slideUp">
          <div
            className={`flex items-center gap-3 rounded-full px-5 py-3 text-sm font-medium text-white shadow-card ${
              snackbar.severity === 'success' ? 'bg-success' : 'bg-danger'
            }`}
          >
            {snackbar.severity === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span>{snackbar.message}</span>
            <button onClick={handleCloseSnackbar} className="opacity-80 hover:opacity-100" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default UpdateCredentialsPage;
